import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rolesApi } from '../../../api/admin/roles';
import { ApiError } from '../../../api/client';
import { DataTable } from '../../../components/DataTable';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  description: z.string().optional(),
});
type FormValues = z.infer<typeof formSchema>;

export function RolesPage() {
  const canManage = usePermission('admin.roles.manage');
  const [modalOpen, setModalOpen] = useState(false);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const rolesQuery = useQuery({ queryKey: ['roles'], queryFn: () => rolesApi.list() });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(formSchema) });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => rolesApi.create({ name: values.name, description: values.description || null }),
    onSuccess: () => {
      showSuccess('Role created.');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      setModalOpen(false);
      reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create role.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => rolesApi.deactivate(id),
    onSuccess: () => {
      showSuccess('Role deactivated.');
      queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    // Surfaces the backend's real message verbatim, e.g. "Cannot
    // deactivate this role: no other active role grants
    // admin.users.manage." - the last-admin-capable-role guard.
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate role.'),
  });

  return (
    <div className="page">
      <div className="page-header">
        <h1>Roles &amp; Permissions</h1>
        {canManage && <button onClick={() => setModalOpen(true)}>New Role</button>}
      </div>

      {rolesQuery.isLoading && <p>Loading…</p>}
      {rolesQuery.isError && <p className="error-text">Could not load roles.</p>}

      {rolesQuery.data && (
        <DataTable
          rows={rolesQuery.data.data}
          rowKey={(r) => r.id}
          emptyMessage="No roles yet."
          columns={[
            { header: 'Name', render: (r) => <Link to={`/admin/roles/${r.id}/permissions`}>{r.name}</Link> },
            { header: 'Description', render: (r) => r.description ?? '-' },
            { header: 'Status', render: (r) => (r.is_active ? 'Active' : 'Inactive') },
            {
              header: '',
              render: (r) =>
                canManage && r.is_active ? (
                  <button className="link-button" onClick={() => deactivateMutation.mutate(r.id)}>Deactivate</button>
                ) : null,
            },
          ]}
        />
      )}

      {modalOpen && (
        <Modal title="New Role" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Name
              <input {...register('name')} />
              {errors.name && <span className="field-error">{errors.name.message}</span>}
            </label>
            <label>
              Description (optional)
              <input {...register('description')} />
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Create'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
